import React, { useState, useEffect, useMemo } from 'react';
import {
  StyleSheet, Text, View, TextInput, TouchableOpacity, ScrollView, ActivityIndicator,
  Modal, KeyboardAvoidingView, Platform,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import Data, { authHeader } from './Data';
import { useScale, ScaleFn } from './common';

const C = {
  bg: '#F9FAFB', card: '#FFFFFF', cardInner: '#F3F4F6',
  border: '#E5E7EB', text: '#111827', textSub: '#4B5563',
  muted: '#9CA3AF', accent: '#38BDF8', ok: '#34D399', warn: '#FB923C', err: '#F87171',
};

type EditReq = {
  IDX: number; RECORD_IDX: number; REASON: string;
  NEW_TYPE: string | null; NEW_SUB_TYPE: string | null;
  NEW_STATUS: number | null;
  NEW_AMOUNT: number | null; NEW_RECORDER: string | null;
  REQ_TYPE?: 'edit' | 'delete';
  REQUESTER: string; STATUS: 'pending' | 'approved' | 'rejected';
  REGDATE: string; RESOLVED_AT: string | null;
  RESOLVER: string | null; ADMIN_MEMO: string | null;
  PERSON_IDX: number; PERSON_NAME: string;
  ORIG_TYPE: string; ORIG_SUB_TYPE: string | null;
  ORIG_STATUS: number | null;
  ORIG_AMOUNT: string | null; ORIG_RECORDER: string;
  ORIG_REGDATE: string;
};

const STATUS_LABEL: Record<number, string> = { 0: '없음', 1: '욕창', 2: '욕창위험', 4: '요로감염' };
const fmtStatus = (s: number | null | undefined) => (s == null ? '-' : (STATUS_LABEL[s] ?? '-'));

type Status = 'pending' | 'approved' | 'rejected' | 'all';

export default function DiaperEditRequests() {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const [status, setStatus] = useState<Status>('pending');
  const [list, setList] = useState<EditReq[]>([]);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState<EditReq | null>(null);
  const [resolver, setResolver] = useState('');
  const [memo, setMemo] = useState('');
  const [msg, setMsg] = useState<{ text: string; err: boolean } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 항상 전체 fetch (카운트 정확성) — 표시는 status 로 클라이언트 필터
  const load = () => {
    setLoading(true);
    Data.fetchData('diaper/editRequestList', {}, (r: EditReq[]) => {
      setList(r || []);
      setLoading(false);
    }, () => setLoading(false));
  };

  useEffect(() => { if (isFocused) load(); }, [isFocused]);

  const counts = useMemo(() => {
    return {
      pending:  list.filter(x => x.STATUS === 'pending').length,
      approved: list.filter(x => x.STATUS === 'approved').length,
      rejected: list.filter(x => x.STATUS === 'rejected').length,
    };
  }, [list]);

  const filteredList = useMemo(() => {
    if (status === 'all') return list;
    return list.filter(x => x.STATUS === status);
  }, [list, status]);

  const openRow = (r: EditReq) => {
    setSelected(r);
    setResolver('');
    setMemo('');
    setMsg(null);
  };
  const close = () => { setSelected(null); setMsg(null); };

  // resolver/memo 가 바뀌면 메시지 자동 초기화 (이전 에러가 남아있는 문제 방지)
  useEffect(() => { if (msg?.err) setMsg(null); }, [resolver]);

  const resolve = async (action: 'approve' | 'reject') => {
    if (submitting || !selected) return;
    setMsg(null);  // 매 시도마다 이전 메시지 초기화
    const r = (resolver || '').trim();
    if (!r) { setMsg({ text: '처리자 이름을 입력해 주세요', err: true }); return; }
    if (!/^[가-힣]{2,5}$/.test(r)) {
      setMsg({ text: `이름 "${r}" 은 한글 2~5자 아님 (공백/영문/숫자 X)`, err: true }); return;
    }
    setSubmitting(true);
    // 직접 fetch 호출 (Data.fetchData 우회) — 응답 명확히 처리
    fetch('/diaper/resolveEditRequest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(await authHeader()) },
      body: JSON.stringify({
        REQUEST_IDX: selected.IDX,
        ACTION: action,
        RESOLVER: r,
        ADMIN_MEMO: (memo || '').trim() || null,
      }),
    })
      .then(res => res.json())
      .then(json => {
        setSubmitting(false);
        if (json && json.result && json.result.STATUS) {
          close();
          load();
        } else {
          setMsg({ text: '처리 실패 — ' + (json?.error || JSON.stringify(json).slice(0, 80)), err: true });
        }
      })
      .catch(e => {
        setSubmitting(false);
        setMsg({ text: '네트워크 오류 — ' + (e?.message || 'unknown'), err: true });
      });
  };

  const fmtDate = (d: string | null) =>
    !d ? '-' : d.replace('T', ' ').slice(0, 16);

  const renderDiff = (r: EditReq) => {
    if (r.REQ_TYPE === 'delete') {
      return (
        <View style={styles.deleteNotice}>
          <Ionicons name="trash-outline" size={s(16)} color={C.err} />
          <Text style={styles.deleteNoticeText}>이 기록의 삭제 요청 — 승인 시 목록/통계에서 제거됩니다</Text>
        </View>
      );
    }
    const parts: { k: string; o: string; n: string }[] = [];
    if (r.NEW_TYPE)      parts.push({ k: '구분',    o: r.ORIG_TYPE,                  n: r.NEW_TYPE });
    if (r.NEW_SUB_TYPE)  parts.push({ k: '세부',    o: r.ORIG_SUB_TYPE || '-',       n: r.NEW_SUB_TYPE });
    if (r.NEW_STATUS != null) parts.push({ k: '병증', o: fmtStatus(r.ORIG_STATUS),    n: fmtStatus(r.NEW_STATUS) });
    if (r.NEW_AMOUNT != null) parts.push({ k: '양',  o: r.ORIG_AMOUNT || '-',         n: String(r.NEW_AMOUNT) });
    if (r.NEW_RECORDER)  parts.push({ k: '기록자',  o: r.ORIG_RECORDER,              n: r.NEW_RECORDER });
    if (parts.length === 0) return <Text style={styles.diffEmpty}>수정안 없음 (상태/사유만)</Text>;
    return (
      <View>
        {parts.map(p => (
          <View key={p.k} style={styles.diffRow}>
            <Text style={styles.diffK}>{p.k}</Text>
            <Text style={styles.diffO}>{p.o}</Text>
            <Ionicons name="arrow-forward" size={s(14)} color={C.muted} style={{ marginHorizontal: s(6) }} />
            <Text style={styles.diffN}>{p.n}</Text>
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={[styles.root, { backgroundColor: C.bg }]}>
      {/* 상태 탭 */}
      <View style={styles.tabRow}>
        {(['pending', 'approved', 'rejected', 'all'] as Status[]).map(t => {
          const lab = t === 'pending' ? `대기 ${counts.pending}`
                    : t === 'approved' ? `승인 ${counts.approved}`
                    : t === 'rejected' ? `거부 ${counts.rejected}` : '전체';
          return (
            <TouchableOpacity
              key={t}
              onPress={() => setStatus(t)}
              style={[styles.tab, status === t && styles.tabActive]}
              activeOpacity={0.7}
            >
              <Text style={[styles.tabText, status === t && styles.tabTextActive]}>{lab}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {loading ? (
        <ActivityIndicator color={C.accent} style={{ marginTop: s(30) }} />
      ) : (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: s(12) }}>
          {filteredList.length === 0 && (
            <Text style={{ textAlign: 'center', color: C.textSub, padding: s(30) }}>
              {status === 'pending' ? '대기 중인 요청 없음' : '요청 없음'}
            </Text>
          )}
          {filteredList.map(r => (
            <TouchableOpacity
              key={r.IDX}
              onPress={() => openRow(r)}
              activeOpacity={0.7}
              style={styles.itemCard}
            >
              <View style={styles.itemHeader}>
                <View style={[styles.statusBadge, {
                  backgroundColor:
                    r.STATUS === 'pending'  ? C.warn :
                    r.STATUS === 'approved' ? C.ok : C.err,
                }]}>
                  <Text style={styles.statusBadgeText}>
                    {r.STATUS === 'pending' ? '대기' : r.STATUS === 'approved' ? '승인' : '거부'}
                  </Text>
                </View>
                {r.REQ_TYPE === 'delete' && (
                  <View style={styles.typeBadge}>
                    <Ionicons name="trash-outline" size={s(12)} color="#FFF" />
                    <Text style={styles.typeBadgeText}>삭제요청</Text>
                  </View>
                )}
                <Text style={styles.itemPerson}>{r.PERSON_NAME}</Text>
                <Text style={styles.itemDate}>{fmtDate(r.REGDATE)}</Text>
              </View>
              <View style={styles.itemBody}>
                <Text style={styles.itemMeta}>
                  원본: {r.ORIG_TYPE}{r.ORIG_SUB_TYPE ? '(' + r.ORIG_SUB_TYPE + ')' : ''} · 병증: {fmtStatus(r.ORIG_STATUS)} · {r.ORIG_AMOUNT || '-'}g · {r.ORIG_RECORDER}
                </Text>
                <Text style={styles.itemReason} numberOfLines={2}>“{r.REASON}”</Text>
                <Text style={styles.itemRequester}>요청자: {r.REQUESTER}</Text>
                {r.STATUS !== 'pending' && (
                  <Text style={styles.itemResolver}>
                    {r.STATUS === 'approved' ? '승인' : '거부'} · {r.RESOLVER || '-'} · {fmtDate(r.RESOLVED_AT)}
                  </Text>
                )}
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      {/* 상세/처리 모달 */}
      <Modal visible={!!selected} transparent animationType="fade" onRequestClose={close}>
        <KeyboardAvoidingView
          style={styles.modalBg}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{selected?.REQ_TYPE === 'delete' ? '삭제' : '수정'} 요청 #{selected?.IDX}</Text>
              <TouchableOpacity onPress={close} hitSlop={{ top: s(12), bottom: s(12), left: s(12), right: s(12) }}>
                <Ionicons name="close" size={s(26)} color={C.textSub} />
              </TouchableOpacity>
            </View>

            {selected && (
              <ScrollView
                style={{ maxHeight: s(560), marginRight: s(-6) }}
                contentContainerStyle={{ paddingRight: s(12), paddingBottom: s(8) }}
              >
                {/* 이용자 + 원본 */}
                <View style={styles.origBox}>
                  <Text style={styles.origLabel}>이용자 · 원본</Text>
                  <Text style={styles.origText}>
                    {selected.PERSON_NAME} · {selected.ORIG_TYPE}{selected.ORIG_SUB_TYPE ? '(' + selected.ORIG_SUB_TYPE + ')' : ''} · 병증: {fmtStatus(selected.ORIG_STATUS)} · {selected.ORIG_AMOUNT || '-'}g · {selected.ORIG_RECORDER}
                  </Text>
                  <Text style={styles.origDate}>{fmtDate(selected.ORIG_REGDATE)}</Text>
                </View>

                {/* 변경 사항 / 삭제 요청 */}
                <Text style={styles.modalLabel}>{selected.REQ_TYPE === 'delete' ? '요청 내용' : '변경 사항'}</Text>
                {renderDiff(selected)}

                {/* 사유 */}
                <Text style={styles.modalLabel}>요청 사유</Text>
                <View style={styles.reasonBox}>
                  <Text style={styles.reasonText}>{selected.REASON}</Text>
                  <Text style={styles.requesterText}>— {selected.REQUESTER} · {fmtDate(selected.REGDATE)}</Text>
                </View>

                {selected.STATUS === 'pending' ? (
                  <>
                    <Text style={styles.modalLabel}>처리자 이름 *</Text>
                    <TextInput
                      value={resolver}
                      onChangeText={setResolver}
                      placeholder="예: 홍길동 (한글 2~5자)"
                      placeholderTextColor={C.muted}
                      style={[
                        styles.modalInput,
                        resolver && !/^[가-힣]{2,5}$/.test(resolver.trim()) && { borderColor: C.err, borderWidth: 1.5 },
                      ]}
                      maxLength={5}
                    />
                    {resolver.trim() && !/^[가-힣]{2,5}$/.test(resolver.trim()) && (
                      <Text style={{ color: C.err, fontSize: s(12), marginTop: s(4) }}>
                        ✗ 한글 2~5자
                      </Text>
                    )}

                    <Text style={styles.modalLabel}>관리자 메모 (선택)</Text>
                    <TextInput
                      value={memo}
                      onChangeText={setMemo}
                      placeholder="처리 사유 / 코멘트"
                      placeholderTextColor={C.muted}
                      style={[styles.modalInput, { minHeight: s(64), textAlignVertical: 'top' }]}
                      multiline
                      maxLength={500}
                    />
                  </>
                ) : (
                  <View style={styles.resolvedBox}>
                    <Text style={styles.resolvedLabel}>
                      {selected.STATUS === 'approved' ? '✓ 승인됨' : '✗ 거부됨'}
                    </Text>
                    <Text style={styles.resolvedDetail}>
                      처리자: {selected.RESOLVER || '-'} · {fmtDate(selected.RESOLVED_AT)}
                    </Text>
                    {selected.ADMIN_MEMO && (
                      <Text style={styles.resolvedMemo}>"{selected.ADMIN_MEMO}"</Text>
                    )}
                  </View>
                )}
              </ScrollView>
            )}

            {/* sticky 메시지 + 버튼 — 항상 모달 하단에 노출 */}
            {selected?.STATUS === 'pending' && (
              <View style={styles.stickyBottom}>
                {msg && (
                  <View style={[styles.modalMsg, { backgroundColor: msg.err ? C.err : C.ok }]}>
                    <Text style={styles.modalMsgText}>{msg.text}</Text>
                  </View>
                )}
                <View style={{ flexDirection: 'row', gap: s(10), marginTop: s(10) }}>
                  <TouchableOpacity
                    onPress={() => resolve('reject')}
                    style={[styles.btnReject, submitting && { opacity: 0.6 }]}
                    disabled={submitting}
                    activeOpacity={0.8}
                  >
                    <Text style={styles.btnRejectText}>{submitting ? '...' : '거부'}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => resolve('approve')}
                    style={[styles.btnApprove, submitting && { opacity: 0.6 }]}
                    disabled={submitting}
                    activeOpacity={0.8}
                  >
                    <Text style={styles.btnApproveText}>{submitting ? '...' : '승인'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  root: { flex: 1 },
  tabRow: { flexDirection: 'row', backgroundColor: C.card, borderBottomWidth: 1, borderBottomColor: C.border, paddingHorizontal: s(8), paddingVertical: s(8), gap: s(6) },
  tab: { paddingHorizontal: s(14), paddingVertical: s(8), borderRadius: s(18), backgroundColor: C.cardInner },
  tabActive: { backgroundColor: C.accent },
  tabText: { color: C.textSub, fontWeight: '600', fontSize: s(14) },
  tabTextActive: { color: '#FFF' },
  itemCard: { backgroundColor: C.card, borderRadius: s(12), padding: s(14), marginBottom: s(10), borderWidth: 1, borderColor: C.border },
  itemHeader: { flexDirection: 'row', alignItems: 'center', gap: s(8), marginBottom: s(8) },
  statusBadge: { paddingHorizontal: s(10), paddingVertical: s(4), borderRadius: s(6) },
  statusBadgeText: { color: '#FFF', fontWeight: '700', fontSize: s(12) },
  typeBadge: { flexDirection: 'row', alignItems: 'center', gap: s(3), backgroundColor: C.err, paddingHorizontal: s(8), paddingVertical: s(4), borderRadius: s(6) },
  typeBadgeText: { color: '#FFF', fontWeight: '700', fontSize: s(12) },
  deleteNotice: { flexDirection: 'row', alignItems: 'center', gap: s(8), backgroundColor: '#FEF2F2', borderWidth: 1, borderColor: C.err, padding: s(12), borderRadius: s(10) },
  deleteNoticeText: { flex: 1, fontSize: s(13), color: C.err, fontWeight: '600' },
  itemPerson: { fontSize: s(16), fontWeight: '700', color: C.text, flex: 1 },
  itemDate: { fontSize: s(12), color: C.textSub },
  itemBody: { gap: s(4) },
  itemMeta: { fontSize: s(13), color: C.textSub },
  itemReason: { fontSize: s(14), color: C.text, fontStyle: 'italic', marginTop: s(2) },
  itemRequester: { fontSize: s(12), color: C.muted, marginTop: s(2) },
  itemResolver: { fontSize: s(12), color: C.textSub, marginTop: s(2) },
  modalBg: { flex: 1, backgroundColor: 'rgba(15,23,42,0.55)', justifyContent: 'center', alignItems: 'center', padding: s(16) },
  modalCard: { width: '100%', maxWidth: s(540), backgroundColor: C.card, borderRadius: s(14), padding: s(18), paddingRight: s(12), overflow: 'hidden', shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: s(16), shadowOffset: { width: 0, height: 8 }, elevation: 6 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: s(12), paddingBottom: s(10), borderBottomWidth: 1, borderBottomColor: C.border },
  modalTitle: { fontSize: s(18), fontWeight: '700', color: C.text },
  origBox: { backgroundColor: C.cardInner, padding: s(12), borderRadius: s(10), marginBottom: s(12) },
  origLabel: { fontSize: s(11), color: C.textSub, fontWeight: '700', marginBottom: s(4) },
  origText: { fontSize: s(14), color: C.text, fontWeight: '600' },
  origDate: { fontSize: s(12), color: C.textSub, marginTop: s(4) },
  modalLabel: { fontSize: s(14), color: C.textSub, marginTop: s(14), marginBottom: s(8), fontWeight: '700' },
  diffRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: s(6), borderBottomWidth: 1, borderBottomColor: C.border },
  diffK: { fontSize: s(13), fontWeight: '700', color: C.textSub, width: s(50) },
  diffO: { fontSize: s(14), color: C.err, flex: 1, textDecorationLine: 'line-through' },
  diffN: { fontSize: s(14), color: C.ok, flex: 1, fontWeight: '700' },
  diffEmpty: { fontSize: s(13), color: C.muted, fontStyle: 'italic', padding: s(8) },
  reasonBox: { backgroundColor: C.cardInner, padding: s(12), borderRadius: s(10) },
  reasonText: { fontSize: s(14), color: C.text, fontStyle: 'italic' },
  requesterText: { fontSize: s(12), color: C.muted, marginTop: s(6), textAlign: 'right' },
  modalInput: { borderWidth: 1, borderColor: C.border, borderRadius: s(8), paddingHorizontal: s(12), paddingVertical: s(10), fontSize: s(15), color: C.text, backgroundColor: C.cardInner },
  modalMsg: { marginTop: s(12), padding: s(10), borderRadius: s(8), alignItems: 'center' },
  modalMsgText: { color: '#FFF', fontWeight: '700', fontSize: s(14) },
  btnReject: { flex: 1, backgroundColor: C.err, paddingVertical: s(14), borderRadius: s(10), alignItems: 'center' },
  btnRejectText: { color: '#FFF', fontWeight: '700', fontSize: s(16) },
  btnApprove: { flex: 2, backgroundColor: C.ok, paddingVertical: s(14), borderRadius: s(10), alignItems: 'center' },
  btnApproveText: { color: '#FFF', fontWeight: '700', fontSize: s(16) },
  resolvedBox: { backgroundColor: C.cardInner, padding: s(14), borderRadius: s(10), marginTop: s(12), alignItems: 'center' },
  resolvedLabel: { fontSize: s(16), fontWeight: '700', color: C.text },
  resolvedDetail: { fontSize: s(13), color: C.textSub, marginTop: s(6) },
  resolvedMemo: { fontSize: s(13), color: C.text, fontStyle: 'italic', marginTop: s(8), textAlign: 'center' },
  stickyBottom: { paddingTop: s(12), marginTop: s(6), borderTopWidth: 1, borderTopColor: C.border, backgroundColor: C.card },
});
